/**
 * Curator Bridge Slice-4 response normalization.
 *
 * Everything the curate_signal API returns is treated as untrusted `unknown`
 * and narrowed here before any component sees it. Queue rows carry
 * `signal_content`, which is collector-derived JSON: its shape is whatever a
 * source produced, not a guarantee. Narrowing at one boundary means a component
 * never branches on a value whose type it assumed.
 *
 * React escapes interpolated text, and this feature renders no HTML - there is
 * no dangerouslySetInnerHTML anywhere in it - so the remaining injection surface
 * is the link. `safeHttpUrl` is what closes it: a `javascript:` or `data:` value
 * arriving in signal_url would otherwise become a clickable script if handed to
 * an anchor href.
 */

import {
  DECISION_KINDS,
  LISTABLE_QUEUE_STATES,
  PUBLICATION_STATES,
  type CuratorQueuePage,
  type CuratorQueueRow,
  type DecisionKind,
  type DecisionResult,
  type LatestDecision,
  type ListableQueueState,
  type PublicationContext,
  type PublicationState,
} from './types';

/** A JSON object, or null for anything else (arrays included). */
export function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Non-empty trimmed text, or null. Numbers and booleans are NOT coerced. */
export function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** A finite non-negative integer, or null. */
export function asCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

/**
 * Canonical non-negative decimal integer text - the shape the server both emits
 * for a bigint and requires back. Rejected rather than coerced: a version that
 * is not canonical text cannot be sent as an expected version, and guessing one
 * is exactly what this slice exists to avoid.
 */
export function asVersionText(value: unknown): string | null {
  if (typeof value === 'string' && /^(0|[1-9][0-9]*)$/.test(value)) return value;
  return null;
}

export function asPublicationState(value: unknown): PublicationState | null {
  return typeof value === 'string' &&
    (PUBLICATION_STATES as readonly string[]).includes(value)
    ? (value as PublicationState)
    : null;
}

export function asListableQueueState(value: unknown): ListableQueueState | null {
  return typeof value === 'string' &&
    (LISTABLE_QUEUE_STATES as readonly string[]).includes(value)
    ? (value as ListableQueueState)
    : null;
}

export function asDecisionKind(value: unknown): DecisionKind | null {
  return typeof value === 'string' &&
    (DECISION_KINDS as readonly string[]).includes(value)
    ? (value as DecisionKind)
    : null;
}

/**
 * An absolute http/https URL, or null.
 *
 * Only these two schemes are returned. `javascript:`, `data:`, `vbscript:` and
 * anything else a collector might have captured are dropped rather than
 * rendered, so a source URL can never become an executable link. Relative URLs
 * are dropped too: a source link that does not say where it points is not a
 * source link.
 */
export function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

function latestDecisionOf(row: Record<string, unknown>): LatestDecision | null {
  const kind = asDecisionKind(row.latest_decision_kind);
  const decidedAt = asText(row.latest_decision_at);
  const reason = asText(row.latest_decision_reason);
  const actor = asText(row.latest_decision_actor_display_name);
  if (kind === null && decidedAt === null && reason === null && actor === null) {
    return null;
  }
  return { kind, reason, actorDisplayName: actor, decidedAt };
}

/**
 * One queue row, or null if it lacks the identity a decision needs.
 *
 * A row without a signal id, a known publication state or a canonical version
 * cannot be acted on - any decision built from it would be a guess - so it is
 * dropped rather than shown with disabled buttons that never explain why.
 */
export function toCuratorQueueRow(value: unknown): CuratorQueueRow | null {
  const row = asRecord(value);
  if (!row) return null;

  const signalId = asText(row.signal_id);
  const publicationState = asPublicationState(row.publication_state);
  const publicationVersion = asVersionText(row.publication_version);
  if (!signalId || !publicationState || publicationVersion === null) return null;

  const content = asRecord(row.signal_content) ?? {};

  return {
    signalId,
    publicationState,
    publicationVersion,
    title: asText(row.feed_title) ?? asText(content.display_title),
    summary: asText(row.feed_summary) ?? asText(content.summary),
    organizationName: asText(row.related_organization_name),
    signalType: asText(row.signal_type),
    sourceSystem: asText(row.signal_source),
    sourceUrl: safeHttpUrl(row.signal_url),
    sourceEventId: asText(row.source_event_id),
    observedAt: asText(row.observed_at),
    firstObservedAt: asText(row.first_observed_at),
    lastObservedAt: asText(row.last_observed_at),
    observedCount: asCount(row.observed_count),
    createdAt: asText(row.created_at),
    whyItMatters: asText(content.why_it_matters),
    recommendedAction: asText(content.recommended_action),
    talkTrack: asText(content.talk_track),
    latestDecision: latestDecisionOf(row),
    decisionCount: asCount(row.decision_count),
  };
}

export function toCuratorQueuePage(
  value: unknown,
  requested: ListableQueueState,
): CuratorQueuePage {
  const payload = asRecord(value) ?? {};
  const rawRows = Array.isArray(payload.signals) ? payload.signals : [];
  const rows: CuratorQueueRow[] = [];
  for (const raw of rawRows) {
    const row = toCuratorQueueRow(raw);
    if (row) rows.push(row);
  }
  return {
    rows,
    publicationState: asListableQueueState(payload.publication_state) ?? requested,
    nextCursor: asText(payload.next_cursor),
  };
}

/**
 * The published-context read, or null if the response is unusable.
 *
 * A context whose state is not `published`, or whose version is not canonical
 * text, is refused rather than repaired: the only reason to call this read is to
 * obtain an expected version, and an expected version that had to be guessed or
 * corrected is worse than none.
 */
export function toPublicationContext(value: unknown): PublicationContext | null {
  const payload = asRecord(value);
  if (!payload) return null;
  const signalId = asText(payload.signal_id);
  const publicationState = asPublicationState(payload.publication_state);
  const publicationVersion = asVersionText(payload.publication_version);
  if (!signalId || publicationState !== 'published' || publicationVersion === null) {
    return null;
  }
  return { signalId, publicationState, publicationVersion };
}

/**
 * The decision result, or null if the response is not usable.
 *
 * The accepted function returns a NESTED envelope -
 * `{ idempotent, decision, signal: { signal_id, publication_state,
 * publication_version } }` - so the outcome fields live under `signal`, not at
 * the top level. Reading them flat yielded nulls for every field while still
 * reporting success, which would tell a curator "Decision recorded" on the
 * strength of a payload nothing had actually understood.
 *
 * It therefore fails closed: a missing or unusable `signal` object returns null,
 * and the caller raises an error rather than rendering a success message. The
 * version must be canonical text; the Edge boundary re-states it exactly from
 * the validated expected version, so a number here means the contract broke.
 */
export function toDecisionResult(value: unknown): DecisionResult | null {
  const payload = asRecord(value);
  if (!payload) return null;
  const signal = asRecord(payload.signal);
  if (!signal) return null;

  const signalId = asText(signal.signal_id);
  const publicationState = asPublicationState(signal.publication_state);
  const publicationVersion = asVersionText(signal.publication_version);
  if (!signalId || !publicationState || publicationVersion === null) return null;

  // `idempotent` decides which of two DIFFERENT things the curator is told: that
  // a decision was recorded, or that an earlier identical one was replayed and
  // nothing was applied twice. A `=== true` test would silently answer "recorded"
  // for a missing, null, string or object value - reporting a first-time decision
  // on the strength of a field the response never actually carried. The accepted
  // contract always sends a boolean, so anything else means the response was not
  // understood, and an unread response is not a success.
  if (typeof payload.idempotent !== 'boolean') return null;

  return {
    signalId,
    publicationState,
    publicationVersion,
    idempotent: payload.idempotent,
  };
}
